import React, { createContext, useState, useContext, useEffect } from 'react';
import { createTheme } from '@mui/material/styles';

export const ThemeContext = createContext();

// Base theme configuration
const baseTheme = {
  breakpoints: {
    values: {
      xs: 0, // Mobile phones
      sm: 768, // Tablets
      md: 992, // Small laptops
      lg: 1024, // Desktops
      xl: 1200, // Large screens
      xxl: 1440, // Extra large screens
    },
  },
  components: {
    MuiContainer: {
      defaultProps: {
        maxWidth: 'xxl',
      },
      styleOverrides: {
        root: {
          paddingLeft: '0 !important',
          paddingRight: '0 !important',
        },
      },
    },
  },
};

// Определение светлой темы
const lightTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'light',
    primary: {
      main: '#4299e1',
      light: '#63b3ed',
      dark: '#3182ce',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ed8936',
      light: '#f6ad55',
      dark: '#dd6b20',
      contrastText: '#ffffff',
    },
    success: {
      main: '#48BB78',
      light: '#68D391',
      dark: '#38A169',
      contrastText: '#ffffff',
    },
    tertiary: {
      main: '#A855F7',
      light: '#C084FC',
      dark: '#9333EA',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f7fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#2d3748',
      secondary: '#718096',
      label: '#2d3748',
    },
    button: {
      secondaryBorder: '#e0e0e0',
      secondaryText: '#718096',
    },
  },
  components: {
    ...baseTheme.components,
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          // scrollbarWidth: 'thin',
          // scrollbarColor: 'rgba(45, 55, 72, 0.3) transparent',
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(45, 55, 72, 0.3)',
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: 'rgba(45, 55, 72, 0.5)',
            },
          },
        },
      },
    },
  },
});

// Определение темной темы
const darkTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'dark',
    primary: {
      main: '#63b3ed',
      light: '#90cdf4',
      dark: '#4299e1',
      contrastText: '#000000',
    },
    secondary: {
      main: '#FE9D29',
      light: '#FFB84D',
      dark: '#E68A00',
      contrastText: '#000000',
    },
    success: {
      main: '#68D391',
      light: '#9AE6B4',
      dark: '#48BB78',
      contrastText: '#000000',
    },
    tertiary: {
      main: '#a855f7',
      light: '#b775f5',
      dark: '#8743c7',
      contrastText: '#000000',
    },
    background: {
      default: '#1a202c',
      paper: '#1f2937',
    },
    text: {
      primary: '#f7fafc',
      secondary: '#cbd5e0',
      label: '#cbd5e0',
    },
    button: {
      secondaryBorder: '#616161',
      secondaryText: '#cbd5e0',
    },
  },
  components: {
    ...baseTheme.components,
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          // scrollbarWidth: 'thin',
          // scrollbarColor: 'rgba(247, 250, 252, 0.2) transparent',
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(247, 250, 252, 0.2)',
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: 'rgba(247, 250, 252, 0.3)',
            },
          },
        },
      },
    },
  },
});

// Провайдер темы
export const ThemeProvider = ({ children }) => {
  // Получаем тему из localStorage или используем светлую по умолчанию
  const [themeType, setThemeType] = useState(
    localStorage.getItem('theme') || 'dark'
  );

  // Проверяем системные настройки
  const prefersDarkMode =
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;

  // Определяем текущую тему
  const theme =
    themeType === 'dark'
      ? darkTheme
      : themeType === 'light'
      ? lightTheme
      : themeType === 'system'
      ? prefersDarkMode
        ? darkTheme
        : lightTheme
      : lightTheme;

  // Функция для переключения темы
  const toggleTheme = (newTheme) => {
    const themeValue = newTheme || (themeType === 'light' ? 'dark' : 'light');
    setThemeType(themeValue);
    localStorage.setItem('theme', themeValue);
  };

  // Обновляем тему при монтировании компонента
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setThemeType(savedTheme);
    }
  }, []);

  // Синхронизируем data-theme атрибут для UI kit CSS
  useEffect(() => {
    const isDarkMode = themeType === 'dark' || (themeType === 'system' && prefersDarkMode);
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [themeType, prefersDarkMode]);

  // Слушаем изменения системной темы
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      if (themeType === 'system') {
        // Если выбрана системная тема, то обновляем при изменении системных настроек
        setThemeType('system');
      }
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [themeType]);

  return (
    <ThemeContext.Provider value={{ theme, themeType, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Хук для использования темы
export const useTheme = () => useContext(ThemeContext);
